import { useState, useRef } from "react";
import {
  MoreVertical, Trash2, Star, StarOff,
  X, ChevronLeft, ChevronRight, ArrowLeft, CheckCircle2,
} from "lucide-react";
import '../../../../assets/css/screens/customer/dashboard/customeraddpaymentcard/CustomerAddPaymentCard.css';
import AppNavbar from '../../../../components/customer/dashboard/apphome/appnavbar/AppNavbar';

/*
  ─────────────────────────────────────────────
  STRIPE INTEGRATION NOTE:
  Replace INITIAL_CARDS with real data from your
  Stripe PaymentMethods API:
    GET /v1/payment_methods?customer=cus_xxx&type=card
  Each card gives you:
    card.last4, card.brand, billing_details.name
  ─────────────────────────────────────────────
*/

const CARD_GRADIENTS = [
  "linear-gradient(135deg,#e05a1f 0%,#e8874e 100%)",
  "linear-gradient(135deg,#0491e1 0%,#06b6d4 100%)",
  "linear-gradient(135deg,#1a1a2e 0%,#484745 100%)",
  "linear-gradient(135deg,#7c3aed 0%,#a78bfa 100%)",
  "linear-gradient(135deg,#065f46 0%,#10b981 100%)",
];

/* Simulated Stripe-added cards — replace with API data */
const INITIAL_CARDS = [
  { id: "pm_1", holder: "Alexandra Johnson", last4: "3413", brand: "visa",   gradient: CARD_GRADIENTS[0], isDefault: true  },
  { id: "pm_2", holder: "Alexandra Johnson", last4: "7821", brand: "master", gradient: CARD_GRADIENTS[1], isDefault: false },
  { id: "pm_3", holder: "John Smith",        last4: "5612", brand: "amex",   gradient: CARD_GRADIENTS[2], isDefault: false },
];

const BRAND_LABEL = { visa: "VISA", master: "MASTER", amex: "AMEX", discover: "DISC" };

const ALL_TXN = [
  { id:  1, icon: "🛒", title: "Grocery Shopping",    sub: "Fresh Mart | 14 Nov 2024",    amount: "+$75.68",    positive: true  },
  { id:  2, icon: "💻", title: "Online Subscription", sub: "Tech News | 13 Nov 2024",     amount: "-$10.99",    positive: false },
  { id:  3, icon: "📢", title: "Adsense Earning",      sub: "Google | 12 Nov 2024",        amount: "+$65.34",    positive: true  },
  { id:  4, icon: "🍽️", title: "Dinner with Friends", sub: "Sky Bistro | 11 Nov 2024",    amount: "-$45.00",    positive: false },
  { id:  5, icon: "⚡",  title: "Electricity Bill",    sub: "LESCO | 10 Nov 2024",         amount: "-$22.50",    positive: false },
  { id:  6, icon: "🏠",  title: "Rent Payment",        sub: "Landlord | 9 Nov 2024",       amount: "-$650.00",   positive: false },
  { id:  7, icon: "🎵",  title: "Spotify Premium",     sub: "Spotify | 8 Nov 2024",        amount: "-$9.99",     positive: false },
  { id:  8, icon: "💼",  title: "Freelance Income",    sub: "Client A | 7 Nov 2024",       amount: "+$320.00",   positive: true  },
  { id:  9, icon: "🏥",  title: "Medical Bills",       sub: "Clinic | 6 Nov 2024",         amount: "-$85.00",    positive: false },
  { id: 10, icon: "☕",  title: "Coffee Shop",         sub: "Café Latte | 5 Nov 2024",     amount: "-$6.50",     positive: false },
  { id: 11, icon: "📦",  title: "Amazon Purchase",     sub: "Amazon | 4 Nov 2024",         amount: "-$129.99",   positive: false },
  { id: 12, icon: "💰",  title: "Salary Credit",       sub: "Employer | 1 Nov 2024",       amount: "+$3,200.00", positive: true  },
];

const TXN_PER_PAGE = 5;

/* ── Confirm Modal ── */
function ConfirmModal({ icon, message, confirmLabel, confirmClass, onConfirm, onClose }) {
  return (
    <div className="cp-overlay" onClick={onClose}>
      <div className="cp-modal cp-modal--sm" onClick={e => e.stopPropagation()}>
        <div className="cp-modal-header">
          <h3>Confirm Action</h3>
          <button className="cp-modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="cp-confirm">
          <div className="cp-confirm-icon">{icon}</div>
          <p dangerouslySetInnerHTML={{ __html: message }} />
          <div className="cp-confirm-btns">
            <button className="cp-btn cp-btn--ghost" onClick={onClose}>Cancel</button>
            <button className={`cp-btn ${confirmClass}`} onClick={onConfirm}>{confirmLabel}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CardPage() {
  const [cards, setCards]           = useState(INITIAL_CARDS);
  const [activeIdx, setActiveIdx]   = useState(0);
  const [menuCardId, setMenuCardId] = useState(null);
  const [menuPos, setMenuPos]       = useState({ top: 0, right: 0 });
  const [modal, setModal]           = useState(null); // { type, card }
  const [toast, setToast]           = useState(null);
  const [txnPage, setTxnPage]       = useState(1);
  const sliderRef = useRef();

  const totalPages = Math.ceil(ALL_TXN.length / TXN_PER_PAGE);
  const pagedTxn   = ALL_TXN.slice((txnPage - 1) * TXN_PER_PAGE, txnPage * TXN_PER_PAGE);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2400);
  };

  const openMenu = (e, id) => {
    e.stopPropagation();
    if (menuCardId === id) { setMenuCardId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    setMenuCardId(id);
  };

  const scrollToCard = (idx) => {
    setActiveIdx(idx);
    const slider = sliderRef.current;
    if (!slider) return;
    slider.children[idx]?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  };

  const handleSetDefault = (id) => {
    setCards(p => p.map(c => ({ ...c, isDefault: c.id === id })));
    showToast("Default card updated ✓");
    setModal(null);
  };

  const handleDelete = (id) => {
    const newCards = cards.filter(c => c.id !== id);
    // if deleted card was default, make first card default
    if (cards.find(c => c.id === id)?.isDefault && newCards.length > 0) {
      newCards[0].isDefault = true;
    }
    setCards(newCards);
    setActiveIdx(i => Math.max(0, i - 1));
    showToast("Card removed");
    setModal(null);
  };

  return (
    <div className="cp-page" onClick={() => setMenuCardId(null)}>

      {/* ── HEADER ── */}
      <div className="cp-header">
        <div className="cp-header-left">
          <button className="cp-back-btn" title="Go back">
            <ArrowLeft size={20} />
          </button>
          <h2 className="cp-page-title">My Cards</h2>
        </div>
      </div>

      {/* ── CARD SLIDER ── */}
      <div className="cp-slider-wrap">
        <div
          className="cp-slider"
          ref={sliderRef}
          onScroll={(e) => {
            const s = e.currentTarget;
            setActiveIdx(Math.round(s.scrollLeft / (s.scrollWidth / cards.length)));
          }}
        >
          {cards.length === 0 ? (
            <div className="cp-empty-cards">
              <span>💳</span>
              <p>No cards added yet.</p>
              <p className="cp-empty-sub">Add a card via the payment screen.</p>
            </div>
          ) : cards.map((card, i) => (
            <div
              key={card.id}
              className={`cp-card${card.isDefault ? " cp-card--default" : ""}`}
              style={{ background: card.gradient }}
              onClick={() => scrollToCard(i)}
            >
              {/* Default badge */}
              {card.isDefault && (
                <div className="cp-card-default-badge">
                  <CheckCircle2 size={12} />
                  <span>Default</span>
                </div>
              )}

              {/* Top row */}
              <div className="cp-card-top">
                <div>
                  <p className="cp-card-label">Card</p>
                  <p className="cp-card-brand">{BRAND_LABEL[card.brand] || card.brand.toUpperCase()}</p>
                </div>
                <button
                  className="cp-card-menu-btn"
                  onClick={(e) => openMenu(e, card.id)}
                  aria-label="Card options"
                >
                  <MoreVertical size={18} color="#fff" />
                </button>
              </div>

              {/* Chip */}
              <div className="cp-card-chip-row">
                <div className="cp-chip" />
              </div>

              {/* Bottom */}
              <div className="cp-card-bottom">
                <div>
                  <p className="cp-card-number">•••• •••• •••• {card.last4}</p>
                  <p className="cp-card-holder">{card.holder}</p>
                </div>
              </div>

              {/* Decorative circle */}
              <div className="cp-card-pattern" />
            </div>
          ))}
        </div>

        {cards.length > 1 && (
          <div className="cp-slider-dots">
            {cards.map((_, i) => (
              <button
                key={i}
                className={`cp-sdot${i === activeIdx ? " cp-sdot--active" : ""}`}
                onClick={() => scrollToCard(i)}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── TRANSACTIONS ── */}
      <div className="cp-txn-section">
        <div className="cp-txn-header">
          <h2>Transaction History</h2>
          <span className="cp-txn-count">{ALL_TXN.length} total</span>
        </div>

        <div className="cp-txn-list">
          {pagedTxn.map(t => (
            <div key={t.id} className="cp-txn-item">
              <div className="cp-txn-icon">{t.icon}</div>
              <div className="cp-txn-info">
                <p className="cp-txn-title">{t.title}</p>
                <p className="cp-txn-sub">{t.sub}</p>
              </div>
              <span className={`cp-txn-amount${t.positive ? " cp-txn-amount--pos" : " cp-txn-amount--neg"}`}>
                {t.amount}
              </span>
            </div>
          ))}
        </div>

        {/* Pagination */}
        <div className="cp-pagination">
          <button className="cp-page-btn" onClick={() => setTxnPage(p => Math.max(1, p - 1))} disabled={txnPage === 1}>
            <ChevronLeft size={18} />
          </button>
          <div className="cp-page-numbers">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
              <button
                key={n}
                className={`cp-page-num${n === txnPage ? " cp-page-num--active" : ""}`}
                onClick={() => setTxnPage(n)}
              >{n}</button>
            ))}
          </div>
          <button className="cp-page-btn" onClick={() => setTxnPage(p => Math.min(totalPages, p + 1))} disabled={txnPage === totalPages}>
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* ── FLOATING CONTEXT MENU ── */}
      {menuCardId && (() => {
        const card = cards.find(c => c.id === menuCardId);
        if (!card) return null;
        return (
          <div
            className="cp-card-menu"
            style={{ top: menuPos.top, right: menuPos.right }}
            onClick={e => e.stopPropagation()}
          >
            {!card.isDefault && (
              <button onClick={() => {
                setMenuCardId(null);
                setModal({ type: "default", card });
              }}>
                <Star size={15} /> Set as Default
              </button>
            )}
            {card.isDefault && (
              <button disabled className="cp-menu-disabled">
                <CheckCircle2 size={15} /> Default Card
              </button>
            )}
            <button
              className="cp-menu-danger"
              onClick={() => {
                setMenuCardId(null);
                setModal({ type: "delete", card });
              }}
            >
              <Trash2 size={15} /> Remove Card
            </button>
          </div>
        );
      })()}

      {/* ── MODALS ── */}
      {modal?.type === "delete" && (
        <ConfirmModal
          icon="🗑️"
          message={`Remove card ending in <strong>${modal.card.last4}</strong>?<br/>This cannot be undone.`}
          confirmLabel="Remove"
          confirmClass="cp-btn--danger"
          onConfirm={() => handleDelete(modal.card.id)}
          onClose={() => setModal(null)}
        />
      )}

      {modal?.type === "default" && (
        <ConfirmModal
          icon="⭐"
          message={`Set card ending in <strong>${modal.card.last4}</strong> as your default payment card?`}
          confirmLabel="Set Default"
          confirmClass="cp-btn--primary"
          onConfirm={() => handleSetDefault(modal.card.id)}
          onClose={() => setModal(null)}
        />
      )}

      {toast && <div className="cp-toast">{toast}</div>}
    </div>
  );
}